import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '@/app/store';
import {
  DEFAULT_CURRENCY_CODE,
  SUPPORTED_CURRENCIES,
  type CurrencyCode,
  type CurrencyOption,
} from '@/constants/currency';
import { getSupportedCurrencies, type CurrencyListPayload } from '@/services/referenceService';

const CURRENCY_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

const cloneCurrencyOptions = (options: ReadonlyArray<CurrencyOption>): CurrencyOption[] =>
  options
    .map((option) => ({
      ...option,
      countries: [...option.countries] as typeof option.countries,
      locales: [...option.locales] as typeof option.locales,
    }))
    .slice() as CurrencyOption[];

export type CurrencyReferenceState = {
  options: CurrencyOption[];
  defaultCurrency: CurrencyCode;
  effectiveDate: string | null;
  updatedAt: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  lastFetchedAt: string | null;
  isFallback: boolean;
};

export type ReferenceState = {
  currency: CurrencyReferenceState;
};

const initialCurrencyState: CurrencyReferenceState = {
  options: cloneCurrencyOptions(SUPPORTED_CURRENCIES),
  defaultCurrency: DEFAULT_CURRENCY_CODE,
  effectiveDate: null,
  updatedAt: null,
  status: 'idle',
  error: null,
  lastFetchedAt: null,
  isFallback: false,
};

const initialState: ReferenceState = {
  currency: initialCurrencyState,
};

type CurrencyFetchResult = {
  payload: CurrencyListPayload;
  options: ReadonlyArray<CurrencyOption>;
  defaultCurrency: CurrencyCode;
  isFallback: boolean;
  errorMessage?: string;
  fetchedAt: string;
};

const shouldSkipFetch = (state: RootState): boolean => {
  const { currency } = state.reference;
  if (currency.status === 'loading') {
    return true;
  }
  if (currency.lastFetchedAt) {
    const age = Date.now() - new Date(currency.lastFetchedAt).getTime();
    if (age < CURRENCY_CACHE_TTL_MS && currency.options.length > 0) {
      return true;
    }
  }
  return false;
};

export const fetchSupportedCurrencies = createAsyncThunk<CurrencyFetchResult, void, { state: RootState }>(
  'reference/fetchSupportedCurrencies',
  async () => {
    try {
      const payload = await getSupportedCurrencies();
      const options = payload.currencies.length > 0 ? payload.currencies : SUPPORTED_CURRENCIES;
      return {
        payload: { ...payload, currencies: options },
        options,
        defaultCurrency: payload.defaultCurrency ?? DEFAULT_CURRENCY_CODE,
        isFallback: false,
        fetchedAt: new Date().toISOString(),
      } satisfies CurrencyFetchResult;
    } catch (error) {
      const options = SUPPORTED_CURRENCIES;
      const fallbackPayload: CurrencyListPayload = {
        defaultCurrency: DEFAULT_CURRENCY_CODE,
        effectiveDate: null,
        updatedAt: null,
        count: options.length,
        currencies: options,
      };
      return {
        payload: fallbackPayload,
        options,
        defaultCurrency: DEFAULT_CURRENCY_CODE,
        isFallback: true,
        errorMessage: error instanceof Error ? error.message : 'Unable to load currencies',
        fetchedAt: new Date().toISOString(),
      } satisfies CurrencyFetchResult;
    }
  },
  {
    condition: (_arg, { getState }) => {
      const state = getState();
      return !shouldSkipFetch(state as RootState);
    },
  },
);

const referenceSlice = createSlice({
  name: 'reference',
  initialState,
  reducers: {
    invalidateCurrencyCache: (state) => {
      state.currency.lastFetchedAt = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchSupportedCurrencies.pending, (state) => {
      state.currency.status = 'loading';
      state.currency.error = null;
    });

    builder.addCase(fetchSupportedCurrencies.fulfilled, (state, action) => {
      const { payload, options, defaultCurrency, fetchedAt, isFallback, errorMessage } = action.payload;
      state.currency.status = 'succeeded';
      state.currency.options = cloneCurrencyOptions(options) as typeof state.currency.options;
      state.currency.defaultCurrency = defaultCurrency;
      state.currency.effectiveDate = payload.effectiveDate;
      state.currency.updatedAt = payload.updatedAt;
      state.currency.lastFetchedAt = fetchedAt;
      state.currency.isFallback = isFallback;
      state.currency.error = isFallback ? errorMessage ?? 'Using fallback currency cache' : null;
    });

    builder.addCase(fetchSupportedCurrencies.rejected, (state, action) => {
      state.currency.status = 'failed';
      state.currency.error = action.error.message ?? 'Unable to load currencies';
    });
  },
});

export const { invalidateCurrencyCache } = referenceSlice.actions;

const selectReferenceState = (state: RootState): ReferenceState => state.reference;
const selectCurrencyReference = (state: RootState): CurrencyReferenceState => selectReferenceState(state).currency;

export const selectCurrencyOptions = (state: RootState): ReadonlyArray<CurrencyOption> => selectCurrencyReference(state).options;
export const selectCurrencyStatus = (state: RootState) => selectCurrencyReference(state).status;
export const selectCurrencyError = (state: RootState) => selectCurrencyReference(state).error;
export const selectCurrencyDefaultFromReference = (state: RootState) => selectCurrencyReference(state).defaultCurrency;
export const selectCurrencyLastFetchedAt = (state: RootState) => selectCurrencyReference(state).lastFetchedAt;

export default referenceSlice.reducer;
